import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { getReviewById, updateModerationReview } from "../../HTTP/articles";
import { updateVisibleReview } from "../../HTTP/admin";
import { FEED_REPORT_REASONS } from "../Common/feedReportReasons";

export default function CheckReview() {
  const user = JSON.parse(localStorage.getItem("user"));
  if (!user?.admin) {
    window.location.href = "/";
  }

  const { id } = useParams();
  const [formData, setFormData] = useState({
    id,
    user_id: user?.id,
    theme: "",
    presentation: "",
    visible: 0,
    articles: [],
  });

  // --- Refus: état du popup + champs ---
  const [isRefuseOpen, setIsRefuseOpen] = useState(false);
  const [selectedReason, setSelectedReason] = useState("");
  const [customReason, setCustomReason] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const funReview = async () => {
      const res = await getReviewById(id);
      setFormData(res);
    };
    funReview();
  }, [id]);

  const handlePublishReview = async () => {
    setSubmitting(true);
    setErrorMsg("");
    try {
      const res = await updateModerationReview(id);
      if (res) {
        alert("Revue acceptée");
        window.location.href = "/admin/reviews";
      } else {
        alert("Une erreur est survenue lors de l’acceptation de la revue");
      }
    } finally {
      setSubmitting(false);
    }
  };

  // --- Modal helpers ---
  const openRefuseModal = () => {
    setSelectedReason("");
    setCustomReason("");
    setErrorMsg("");
    setIsRefuseOpen(true);
  };
  const closeRefuseModal = () => setIsRefuseOpen(false);

  // Focus / clavier (Esc) quand la modale est ouverte
  const modalRef = useRef(null);
  useEffect(() => {
    function onKeyDown(e) {
      if (e.key === "Escape") closeRefuseModal();
    }
    if (isRefuseOpen) {
      document.addEventListener("keydown", onKeyDown);
      setTimeout(() => modalRef.current?.focus(), 0);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = "";
    };
  }, [isRefuseOpen]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) closeRefuseModal();
  };

  const handleConfirmRefuse = async () => {
    setErrorMsg("");

    if (!selectedReason) {
      setErrorMsg("Veuillez sélectionner un motif de refus.");
      return;
    }

    const isOther = selectedReason.startsWith("Autre");
    const reasonToSend = isOther ? customReason.trim() : selectedReason;

    if (isOther && reasonToSend.length === 0) {
      setErrorMsg("Veuillez préciser le motif dans le champ texte.");
      return;
    }

    setSubmitting(true);
    try {
      // visible à 0 + commentaire (motif) en 3e argument
      const res1 = await updateVisibleReview(id, 0, reasonToSend);
      const res2 = await updateModerationReview(id);
      const res = res1 && res2;

      if (res) {
        alert("Revue refusée");
        window.location.href = "/admin/reviews";
      } else {
        alert("Une erreur est survenue lors du refus de la revue");
      }
    } finally {
      setSubmitting(false);
      closeRefuseModal();
    }
  };

  if (formData.visible === 0 && user?.admin === 0 && user?.id !== formData.user_id) {
    window.location.href = "/reviews";
  }

  return (
    <>
      <div
        className="mt-8 max-w-3xl mx-auto p-6 bg-white rounded-lg shadow-md md:overflow-y-auto"
        style={{ maxHeight: window.innerWidth >= 768 ? "calc(100vh - 100px)" : undefined }}
      >
        <h1 className="text-2xl font-semibold text-gray-800 text-center">Vérification de la revue</h1>

        <h2 className="text-xl font-semibold text-gray-800 mt-8 text-center">Titre de la revue</h2>
        <h3 className="text-lg font-semibold text-gray-800 mt-2 text-center notranslate">
          {formData.theme}
        </h3>

        <h2 className="text-xl font-semibold text-gray-800 mt-8 text-center">Description de la revue</h2>
        <div
          className="max-w-2xl mx-auto mt-4 p-6 bg-white shadow-md rounded max-h-[60vh] overflow-y-auto notranslate"
          dangerouslySetInnerHTML={{ __html: formData.presentation }}
        />

        <h4 className="text-lg font-semibold text-gray-800 mt-6 text-center notranslate">
          Articles associés
        </h4>
        <ul className="list-disc list-inside max-w-2xl mx-auto mt-4 p-6 bg-white shadow-md rounded max-h-[40vh] overflow-y-auto">
          {formData.articles?.map((article) => (
            <li key={article.id} className="mb-4">
              <a href={article.link} className="font-medium" target="_blank" rel="noreferrer">
                {article.title}{" "}
                <span className="text-sm text-gray-600">
                  - {new Date(article.publication_date).toLocaleDateString()}
                </span>
              </a>
              {article.image && (
                <img
                  src={article.image}
                  alt={article.title}
                  className="mt-1 w-full h-auto max-h-48 object-cover rounded"
                />
              )}
            </li>
          ))}
        </ul>

        {/* Actions principales */}
        <div className="max-w-2xl mx-auto flex flex-col items-center">
          <div className="flex flex-col sm:flex-row sm:justify-center gap-3 mt-6">
            <button
              className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-60"
              onClick={handlePublishReview}
              disabled={submitting}
            >
              Accepter
            </button>
            <button
              className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-60"
              onClick={openRefuseModal}
              disabled={submitting}
            >
              Refuser
            </button>
          </div>
        </div>
      </div>

      {/* --- MODALE DE REFUS --- */}
      {isRefuseOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onMouseDown={handleBackdropClick}
          aria-modal="true"
          role="dialog"
          aria-labelledby="refuse-title"
          aria-describedby="refuse-desc"
        >
          <div
            ref={modalRef}
            tabIndex={-1}
            className="w-full max-w-xl bg-white rounded-xl shadow-xl outline-none"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-4 border-b flex items-center justify-between">
              <h2 id="refuse-title" className="text-lg font-semibold text-gray-800">
                Refuser la revue
              </h2>
              <button
                onClick={closeRefuseModal}
                className="p-2 rounded hover:bg-gray-100"
                aria-label="Fermer"
              >
                ✕
              </button>
            </div>

            <div className="px-5 py-4 space-y-4">
              <p id="refuse-desc" className="text-sm text-gray-600">
                Sélectionnez un motif de refus. Si vous choisissez “Autre problème”, indiquez une
                courte explication.
              </p>

              <fieldset className="space-y-3">
                {FEED_REPORT_REASONS.map(({ label }) => (
                  <label key={label} className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="radio"
                      name="refusalReason"
                      value={label}
                      checked={selectedReason === label}
                      onChange={(e) => {
                        setSelectedReason(e.target.value);
                        setErrorMsg("");
                      }}
                      className="mt-1"
                    />
                    <span className="text-gray-800">{label}</span>
                  </label>
                ))}
              </fieldset>

              {selectedReason.startsWith("Autre") && (
                <div>
                  <label className="block text-sm text-gray-600 mb-2">Précision du motif</label>
                  <textarea
                    className="w-full border rounded-md p-3 focus:outline-none focus:ring"
                    rows={4}
                    placeholder="Expliquez brièvement la raison du refus…"
                    value={customReason}
                    onChange={(e) => setCustomReason(e.target.value)}
                  />
                </div>
              )}

              {errorMsg && <p className="text-red-600 text-sm">{errorMsg}</p>}
            </div>

            <div className="px-5 py-4 border-t flex gap-3 justify-end">
              <button
                onClick={closeRefuseModal}
                className="px-4 py-2 rounded border border-gray-300 hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                onClick={handleConfirmRefuse}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-60"
                disabled={submitting}
              >
                Confirmer le refus
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}